import { useSyncExternalStore } from 'react';

import {
	SMART_FOLDER_ROVO_CONNECTOR_PROVIDERS,
	type SmartFolderRovoConnectorProvider,
} from './SmartFolderRovoConnectorProviders.ts';

const SMART_FOLDER_PROPERTIES_KEY = 'smartFolderProperties';
const DEFAULT_PROVIDER = SMART_FOLDER_ROVO_CONNECTOR_PROVIDERS['google-drive'];

type ContentPropertyNode = {
	readonly key?: string | null;
	readonly value?: string | null;
} | null;

type SmartFolderContentProperties = {
	readonly containerApp?: unknown;
};

export type SmartFolderQuickActionSource = {
	appName: string;
	provider: SmartFolderRovoConnectorProvider;
};

type UseSmartFolderQuickActionSourceOptions = {
	skip?: boolean;
};

const providerByAppName = Object.values(SMART_FOLDER_ROVO_CONNECTOR_PROVIDERS).reduce(
	(acc, providerConfig) => {
		acc[providerConfig.displayName] = providerConfig.provider;
		return acc;
	},
	{} as Record<string, SmartFolderRovoConnectorProvider>,
);

const rememberedSources = new Map<string, SmartFolderQuickActionSource>();
const subscribers = new Set<() => void>();

const subscribe = (callback: () => void) => {
	subscribers.add(callback);

	return () => {
		subscribers.delete(callback);
	};
};

const skipSubscribe = () => () => {};
const getSkippedRememberedSource = () => undefined;

const notifySubscribers = () => {
	subscribers.forEach((callback) => callback());
};

const getContainerApp = (propertyValue: string): string | undefined => {
	try {
		const parsedValue = JSON.parse(propertyValue) as SmartFolderContentProperties;
		return typeof parsedValue.containerApp === 'string' ? parsedValue.containerApp : undefined;
	} catch {
		return undefined;
	}
};

const getSourceFromContainerApp = (
	containerApp?: string,
): SmartFolderQuickActionSource | undefined => {
	if (!containerApp) {
		return undefined;
	}

	const provider = providerByAppName[containerApp];

	return provider
		? {
				appName: containerApp,
				provider,
			}
		: undefined;
};

const getSourceFromProperties = (
	properties?: readonly ContentPropertyNode[] | null,
): SmartFolderQuickActionSource | undefined => {
	const smartFolderProperties = properties?.find(
		(property) => property?.key === SMART_FOLDER_PROPERTIES_KEY,
	);
	const containerApp = smartFolderProperties?.value
		? getContainerApp(smartFolderProperties.value)
		: undefined;

	return getSourceFromContainerApp(containerApp);
};

export const rememberSmartFolderQuickActionSource = ({
	containerApp,
	folderId,
}: {
	containerApp?: string;
	folderId: string;
}) => {
	const source = getSourceFromContainerApp(containerApp);
	if (!source) {
		return;
	}

	rememberedSources.set(folderId, source);
	notifySubscribers();
};

export const getSmartFolderQuickActionSource = (
	properties?: readonly ContentPropertyNode[] | null,
): SmartFolderQuickActionSource =>
	getSourceFromProperties(properties) ?? {
		appName: DEFAULT_PROVIDER.displayName,
		provider: DEFAULT_PROVIDER.provider,
	};

export const useSmartFolderQuickActionSource = (
	folderId: string,
	properties?: readonly ContentPropertyNode[] | null,
	options: UseSmartFolderQuickActionSourceOptions = {},
): SmartFolderQuickActionSource => {
	const { skip = false } = options;
	const rememberedSource = useSyncExternalStore(
		skip ? skipSubscribe : subscribe,
		skip ? getSkippedRememberedSource : () => rememberedSources.get(folderId),
		getSkippedRememberedSource,
	);

	if (skip) {
		return {
			appName: DEFAULT_PROVIDER.displayName,
			provider: DEFAULT_PROVIDER.provider,
		};
	}

	return (
		getSourceFromProperties(properties) ??
		rememberedSource ?? {
			appName: DEFAULT_PROVIDER.displayName,
			provider: DEFAULT_PROVIDER.provider,
		}
	);
};
